import { Box, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { DashboardChart } from "Components/Dashboard/DashboardChart";
import { MetricItem } from "Components/Dashboard/MetricItem";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as ProgressIcon } from "Components/_Shared/Icons/Assets/progress.svg";
import { ReactComponent as UnavailableIcon } from "Components/_Shared/Icons/Assets/unavailable.svg";
import { AuthenticatedLayout } from "Components/_Shared/Layouts/AuthenticatedLayout";
import { TabFilter } from "Components/_Shared/TabFilter";
import { useGetDashboardGraph, useGetDashboardSummary } from "Infrastructure/ApiZfp/AnalyzerApi";
import { GetDashboardGraphInterval } from "Infrastructure/ApiZfp/Schema";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useState } from "react";

export const DashboardPage: React.FunctionComponent = () => {
  const { t } = useResource();
  const [interval, setInterval] = useState<GetDashboardGraphInterval>(GetDashboardGraphInterval.all);
  const { user } = useUserContext();

  const { data: summaryData, isPending: isSummaryPending } = useGetDashboardSummary();
  const { data: graphData } = useGetDashboardGraph({ interval });

  const handleChange = (_: React.SyntheticEvent, index: GetDashboardGraphInterval) => {
    setInterval(index);
  };

  return (
    <AuthenticatedLayout title={t(Resources.Dashboard.Title)}>
      <Stack spacing={Spacing.XXXL}>
        <Stack spacing={Spacing.S}>
          <Typography variant="h2">
            {t(Resources.Dashboard.Subtitle)}, {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="body1" color="secondary">
            {t(Resources.Dashboard.ActivityOverview)}
          </Typography>
        </Stack>
        <Grid container spacing={Spacing.XXL}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <MetricItem
              metricName={t(Resources.AnalysesListing.Status.InProgress)}
              metricValue={summaryData?.inProgress.count || 0}
              changeInPercent={summaryData?.inProgress.monthlyChange || 0}
              loading={isSummaryPending}
              icon={<ProgressIcon />}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <MetricItem
              metricName={t(Resources.AnalysesListing.Status.Completed)}
              metricValue={summaryData?.completed.count || 0}
              changeInPercent={summaryData?.completed.monthlyChange || 0}
              loading={isSummaryPending}
              icon={<CheckIcon />}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <MetricItem
              metricName={t(Resources.AnalysesListing.Status.Declined)}
              metricValue={summaryData?.declined.count || 0}
              changeInPercent={summaryData?.declined.monthlyChange || 0}
              loading={isSummaryPending}
              icon={<UnavailableIcon />}
            />
          </Grid>
        </Grid>
        {!!graphData ? (
          <DashboardChart
            data={graphData}
            interval={interval}
            renderControls={() => {
              return (
                <Box marginBottom={Spacing.M}>
                  <TabFilter value={interval} onChange={handleChange}>
                    <TabFilter.Item label={t(Resources.Dashboard.GraphInterval.AllTime)} value={GetDashboardGraphInterval.all} />
                    <TabFilter.Item label={t(Resources.Dashboard.GraphInterval.TwelveMonths)} value={GetDashboardGraphInterval.year} />
                    <TabFilter.Item label={t(Resources.Dashboard.GraphInterval.SixMonths)} value={GetDashboardGraphInterval.half_year} />
                    <TabFilter.Item label={t(Resources.Dashboard.GraphInterval.ThirtyDays)} value={GetDashboardGraphInterval.month} />
                    <TabFilter.Item label={t(Resources.Dashboard.GraphInterval.SevenDays)} value={GetDashboardGraphInterval.week} />
                  </TabFilter>
                </Box>
              );
            }}
          />
        ) : (
          <Skeleton height={280} />
        )}
      </Stack>
    </AuthenticatedLayout>
  );
};
